import { FC, RefObject, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import * as vp from 'vopay';
import { withStyles, objectEntries } from 'vopay';

import { Title } from 'UI';
import PlanSettingCard, { PlanSettingCardHandle } from '../../utils/PlanSettingCard/PlanSettingCard';
import { PLAN_MODEL_KEYS, sectionHasCurrencyAllowedPermission } from '../../utils/planSettingHelpers';

import styles from './PaymentRails.module.scss';

const cn = withStyles(styles);

export type PaymentRailsHandle = {
  touchAllFields: () => void;
};

export type PaymentRailsProps = {
  ref?: RefObject<PaymentRailsHandle | null>;
  data: vp.PlanSettings | undefined;
  currency: string;
  country: string;
  planType: 'pay as you go' | 'subscription';
  useProgressivePricing: boolean;
  onValidChange: (isValid: boolean) => void;
  onValuesChange: (values: Record<string, string | boolean>) => void;
};

type FlowGroup = 'send' | 'collect' | 'other';

const FLOW_GROUP_ORDER: FlowGroup[] = ['send', 'collect', 'other'];

function getFlowGroup(flowType: vp.PlanSetting['flowType']): FlowGroup {
  if (flowType === 'send') return 'send';
  if (flowType === 'collect') return 'collect';

  return 'other';
}

const PaymentRails: FC<PaymentRailsProps> = ({
  ref,
  data,
  currency,
  country,
  planType,
  useProgressivePricing,
  onValidChange,
  onValuesChange,
}) => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'R1AccountCreation.Billing.PackageDetails.PaymentRails',
  });

  const services = useMemo(
    () =>
      data
        ? objectEntries(data).filter(
            ([key, ps]) =>
              !PLAN_MODEL_KEYS.has(key) && sectionHasCurrencyAllowedPermission(ps.permissions, currency, country),
          )
        : [],
    [data, currency, country],
  );

  const groups = useMemo(() => {
    const map = new Map<FlowGroup, [string, vp.PlanSetting][]>();
    for (const group of FLOW_GROUP_ORDER) map.set(group, []);

    for (const [name, planSetting] of services) {
      map.get(getFlowGroup(planSetting.flowType))!.push([name, planSetting]);
    }

    return map;
  }, [services]);

  const orderedServices = useMemo(() => FLOW_GROUP_ORDER.flatMap((group) => groups.get(group) ?? []), [groups]);

  const cardRefs = useRef<Map<string, PlanSettingCardHandle>>(new Map());
  const validityMapRef = useRef<Record<string, boolean>>({});
  const valuesMapRef = useRef<Record<string, Record<string, string | boolean>>>({});

  const handleServiceValidChange = useCallback(
    (serviceName: string, isValid: boolean) => {
      validityMapRef.current = { ...validityMapRef.current, [serviceName]: isValid };
      onValidChange(services.every(([name]) => validityMapRef.current[name] === true));
    },
    [services, onValidChange],
  );

  const handleServiceValuesChange = useCallback(
    (serviceName: string, values: Record<string, string | boolean>) => {
      valuesMapRef.current = { ...valuesMapRef.current, [serviceName]: values };
      const merged = Object.values(valuesMapRef.current).reduce((acc, v) => ({ ...acc, ...v }), {});
      onValuesChange(merged);
    },
    [onValuesChange],
  );

  useEffect(() => {
    if (services.length === 0) onValidChange(true);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      touchAllFields: () => {
        for (const handle of cardRefs.current.values()) handle.touchAllFields();
      },
    }),
    [],
  );

  return (
    <div>
      {FLOW_GROUP_ORDER.map((group) => {
        const items = groups.get(group) ?? [];
        if (items.length === 0) return null;
        const isFirstGroup = FLOW_GROUP_ORDER.find((g) => (groups.get(g) ?? []).length > 0) === group;

        return (
          <div key={group}>
            <Title level="5" className={cn('groupLabel', isFirstGroup && 'groupLabel--first')}>
              {t(group === 'send' ? 'sendRails' : group === 'collect' ? 'collectRails' : 'other', {
                defaultValue: group === 'send' ? 'Send Rails' : group === 'collect' ? 'Collect Rails' : 'Other',
              })}
            </Title>

            <div className={cn('container')}>
              {items.map(([name, planSetting]) => (
                <div
                  key={name}
                  onFocus={(e) => {
                    if (e.currentTarget.contains(e.relatedTarget as Node)) return;

                    orderedServices
                      .slice(
                        0,
                        orderedServices.findIndex(([n]) => n === name),
                      )
                      .forEach(([n]) => cardRefs.current.get(n)?.touchAllFields());
                  }}
                >
                  <PlanSettingCard
                    ref={(el) => {
                      if (el) cardRefs.current.set(name, el);
                      else cardRefs.current.delete(name);
                    }}
                    serviceName={name}
                    planSetting={planSetting}
                    currency={currency}
                    country={country}
                    planType={planType}
                    useProgressivePricing={useProgressivePricing}
                    onValidChange={handleServiceValidChange}
                    onValuesChange={handleServiceValuesChange}
                  />
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PaymentRails;
